import type { SweepPoint } from '../ble/toolbox-protocol';
import type { SweepCompletionReason } from '../flows/sweep-runner';

export const DIAGNOSTIC_REPORT_SCHEMA_VERSION = 'seaus.diagnostic-report.v2';
export const DIAGNOSTIC_ENVELOPE_SCHEMA_VERSION = 'seaus.diagnostic.v1';
export const DIAGNOSTIC_OBFUSCATION_METHOD = 'xor-gzip-base64';
export const DEFAULT_DIAGNOSTIC_OBFUSCATION_KEY_ID = 'support-v1';

export type DiagnosticMetadataValue = string | number | boolean;
export type DiagnosticMetadata = Record<string, DiagnosticMetadataValue>;

export type DiagnosticSweepSummary = {
  spec: {
    startHz: number;
    endHz: number;
    stepHz: number;
    dwellMs: number;
  };
  completionReason: SweepCompletionReason;
  startedAtMs: number;
  endedAtMs: number;
  pointCount: number;
};

export type DiagnosticTargetSweepStatus = 'completed' | 'failed' | 'skipped';

export type DiagnosticTargetSweep = {
  targetId: string;
  description: string;
  priority: number;
  centerFrequencyHz: number;
  searchBandwidthHz: number;
  status: DiagnosticTargetSweepStatus;
  error: string | null;
  sweep: DiagnosticSweepSummary | null;
  points: SweepPoint[];
};

export type DiagnosticReportSummary = {
  totalTargets: number;
  completedTargets: number;
  failedTargets: number;
  skippedTargets: number;
  totalPointCount: number;
};

export type DiagnosticReportPayload = {
  schemaVersion: typeof DIAGNOSTIC_REPORT_SCHEMA_VERSION;
  generatedAtIso: string;
  fileName: string;
  metadata: DiagnosticMetadata;
  targetSweeps: Record<string, DiagnosticTargetSweep>;
  summary: DiagnosticReportSummary;
};

export type DiagnosticEnvelope = {
  schemaVersion: typeof DIAGNOSTIC_ENVELOPE_SCHEMA_VERSION;
  keyId: string;
  payload: string;
};

export type DiagnosticDownloadDocument = DiagnosticReportPayload | DiagnosticEnvelope;

export type BuildDiagnosticReportPayloadInput = {
  metadata: DiagnosticMetadata;
  targetSweeps: Record<string, DiagnosticTargetSweep>;
  summary: DiagnosticReportSummary;
  fileName: string;
  generatedAtIso?: string;
};

export type BuildDiagnosticDownloadJsonInput = {
  report: DiagnosticReportPayload;
  obfuscationSecret?: string | null;
  obfuscationKeyId?: string | null;
};

export type DiagnosticDownloadMode = 'plain' | typeof DIAGNOSTIC_OBFUSCATION_METHOD;

export type BuildDiagnosticDownloadJsonResult = {
  json: string;
  mode: DiagnosticDownloadMode;
  keyId: string | null;
};

export function buildDiagnosticReportPayload(
  input: BuildDiagnosticReportPayloadInput
): DiagnosticReportPayload {
  const generatedAtIso =
    input.generatedAtIso ??
    (typeof input.metadata.generated_at_iso === 'string'
      ? input.metadata.generated_at_iso
      : new Date().toISOString());

  return {
    schemaVersion: DIAGNOSTIC_REPORT_SCHEMA_VERSION,
    generatedAtIso,
    fileName: input.fileName,
    metadata: input.metadata,
    targetSweeps: input.targetSweeps,
    summary: input.summary,
  };
}

export function buildDiagnosticJson(payload: DiagnosticReportPayload): string {
  return `${JSON.stringify(payload, null, 2)}\n`;
}

export async function buildDiagnosticDownloadJson(
  input: BuildDiagnosticDownloadJsonInput
): Promise<BuildDiagnosticDownloadJsonResult> {
  const secret = input.obfuscationSecret?.trim() ?? '';
  if (!secret) {
    return {
      json: buildDiagnosticJson(input.report),
      mode: 'plain',
      keyId: null,
    };
  }

  const keyId = resolveDiagnosticObfuscationKeyId(input.obfuscationKeyId);
  const key = await deriveDiagnosticObfuscationKey(secret, keyId);
  const reportBytes = new TextEncoder().encode(buildDiagnosticJson(input.report));
  const gzipped = await gzipBytes(reportBytes);
  const obfuscated = xorWithKey(gzipped, key);

  const envelope: DiagnosticEnvelope = {
    schemaVersion: DIAGNOSTIC_ENVELOPE_SCHEMA_VERSION,
    keyId,
    payload: bytesToBase64(obfuscated),
  };

  return {
    json: `${JSON.stringify(envelope, null, 2)}\n`,
    mode: DIAGNOSTIC_OBFUSCATION_METHOD,
    keyId,
  };
}

export async function decodeDiagnosticDownloadJson(
  downloadJson: string,
  obfuscationSecret?: string | null
): Promise<DiagnosticReportPayload> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(downloadJson);
  } catch (error) {
    throw new Error(`Invalid diagnostic download JSON: ${getErrorMessage(error)}`);
  }

  if (!isRecord(parsed) || typeof parsed.schemaVersion !== 'string') {
    throw new Error('Invalid diagnostic download format: missing schemaVersion.');
  }

  if (parsed.schemaVersion === DIAGNOSTIC_REPORT_SCHEMA_VERSION) {
    return parsed as DiagnosticReportPayload;
  }

  if (parsed.schemaVersion !== DIAGNOSTIC_ENVELOPE_SCHEMA_VERSION) {
    throw new Error(`Unsupported diagnostic download schema: ${parsed.schemaVersion}`);
  }

  const envelope = parseDiagnosticEnvelope(parsed);
  const secret = obfuscationSecret?.trim() ?? '';
  if (!secret) {
    throw new Error('Diagnostic obfuscation secret is required to decode this report.');
  }

  const key = await deriveDiagnosticObfuscationKey(secret, envelope.keyId);
  const obfuscatedBytes = base64ToBytes(envelope.payload);
  const gzippedBytes = xorWithKey(obfuscatedBytes, key);
  const plainBytes = await gunzipBytes(gzippedBytes);
  const plainJson = new TextDecoder().decode(plainBytes);

  let decodedPayload: unknown;
  try {
    decodedPayload = JSON.parse(plainJson);
  } catch (error) {
    throw new Error(`Decoded report is not valid JSON: ${getErrorMessage(error)}`);
  }

  if (!isRecord(decodedPayload) || decodedPayload.schemaVersion !== DIAGNOSTIC_REPORT_SCHEMA_VERSION) {
    throw new Error('Decoded report does not match diagnostic report schema.');
  }

  return decodedPayload as DiagnosticReportPayload;
}

export function resolveDiagnosticObfuscationKeyId(keyId?: string | null): string {
  const normalized = keyId?.trim() ?? '';
  return normalized || DEFAULT_DIAGNOSTIC_OBFUSCATION_KEY_ID;
}

export async function deriveDiagnosticObfuscationKey(
  secret: string,
  keyId: string
): Promise<Uint8Array> {
  if (!secret) {
    throw new Error('Diagnostic obfuscation secret is required.');
  }
  const normalizedKeyId = resolveDiagnosticObfuscationKeyId(keyId);
  const seed = new TextEncoder().encode(`${secret}:${normalizedKeyId}`);
  const digest = await crypto.subtle.digest('SHA-256', seed);
  return new Uint8Array(digest);
}

function parseDiagnosticEnvelope(value: Record<string, unknown>): DiagnosticEnvelope {
  const legacyObfuscation = isRecord(value.obfuscation) ? value.obfuscation : null;
  const rootKeyId = typeof value.keyId === 'string' ? value.keyId : '';
  const legacyKeyId = legacyObfuscation && typeof legacyObfuscation.keyId === 'string'
    ? legacyObfuscation.keyId
    : '';
  const keyId = rootKeyId || legacyKeyId;

  if (legacyObfuscation) {
    const method = legacyObfuscation.method;
    if (method !== DIAGNOSTIC_OBFUSCATION_METHOD) {
      throw new Error(`Unsupported diagnostic obfuscation method: ${String(method)}`);
    }
  }
  const payload = value.payload;

  if (typeof keyId !== 'string' || keyId.length === 0) {
    throw new Error('Invalid diagnostic envelope: missing keyId.');
  }
  if (typeof payload !== 'string' || payload.length === 0) {
    throw new Error('Invalid diagnostic envelope: missing payload.');
  }

  return {
    schemaVersion: DIAGNOSTIC_ENVELOPE_SCHEMA_VERSION,
    keyId,
    payload,
  };
}

async function gzipBytes(data: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream !== 'function') {
    throw new Error('CompressionStream is not available in this environment.');
  }
  const stream = new CompressionStream('gzip');
  return transformBytesWithStream(stream, data);
}

async function gunzipBytes(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream !== 'function') {
    throw new Error('DecompressionStream is not available in this environment.');
  }
  const stream = new DecompressionStream('gzip');
  return transformBytesWithStream(stream, data);
}

async function transformBytesWithStream(
  stream: CompressionStream | DecompressionStream,
  data: Uint8Array
): Promise<Uint8Array> {
  // Use a streaming pipe so reads happen while writes are in flight.
  // Writing the full payload first can stall on backpressure in some browsers.
  const source = new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(data);
      controller.close();
    },
  });
  const transformed = source.pipeThrough(stream);
  const output = await new Response(transformed).arrayBuffer();
  return new Uint8Array(output);
}

function xorWithKey(data: Uint8Array, key: Uint8Array): Uint8Array {
  if (key.length === 0) {
    throw new Error('Obfuscation key is empty.');
  }
  const output = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i += 1) {
    output[i] = data[i] ^ key[i % key.length];
  }
  return output;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, Math.min(index + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  let binary = '';
  try {
    binary = atob(value);
  } catch (error) {
    throw new Error(`Invalid base64 payload: ${getErrorMessage(error)}`);
  }

  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
